import React, { useRef, useState, useEffect } from "react";
import { useFileFormConfig } from "./CourseFileFieldsConfig";
import { addCoursesFile, updateCoursesFile } from "../../services/CourseFileService";
import { Toast } from "primereact/toast";
import FormField from "../../components/Form/FormField";
import "../../components/Form/FormField.scss";
import "../../components/Form/Form.scss";
import { Button } from "primereact/button";

function CourseFileForm({ mode, courseFile, onClose, onFormSubmit }) {
  const toast = useRef(null);
  const fieldRefs = useRef({});
  const {
    formTitle,
    initialCommonData,
    initialContentData,
    commonFields,
    contentFields,
  } = useFileFormConfig(courseFile, mode);

  const [commonFormData, setCommonFormData] = useState(initialCommonData);
  const [contentForms, setContentForms] = useState([{ ...initialContentData }]);
  const [hasAddedContent, setHasAddedContent] = useState(false);
  useEffect(() => {
    if (mode === "update" && courseFile) {
      const updatedCommonData = {
        name: courseFile.CourseFiletranslations?.[0]?.name || "",
        description: courseFile.CourseFiletranslations?.[0]?.description || "",
        courseId: courseFile?.Topic?.topicModule?.course_id || null,
        moduleId: courseFile?.Topic?.module_id || null,
        topicId: courseFile.topic_id || null,
      };
      const updatedContentData = courseFile.CourseFiletranslations?.map((translation) => {
        const contentType = translation.content_type || "";
        const contentData = {
          laungaugeId: translation.language_id || null,
          tags: translation.tags || [],
          contentType: contentType,
          thumbnailPath: translation.thumbnail_path || "",
        };

        if (contentType === "Youtube") {
          contentData.path = translation.path || "";
        }
        if (contentType === "Custom") {
          contentData.video = translation.path || "";
        }
        return contentData;
      }) || [{ ...initialContentData }];

      setCommonFormData(updatedCommonData);
      setContentForms(updatedContentData);
    }
  }, [mode, courseFile]);

  const handleCommonChange = (key, value) => {
    if (key === "courseId" && mode === 'update') {
      setCommonFormData((prev) => ({
        ...prev,
        courseId: value,
        moduleId: null,
        topicId: null,
      }));
    } else if (key === "moduleId" && mode === 'update') {
      setCommonFormData((prev) => ({
        ...prev,
        moduleId: value,
        topicId: null,
      }));
    } else {
      setCommonFormData((prev) => ({ ...prev, [key]: value }));
    }
  };
  const handleClear = () => {
    setCommonFormData(initialCommonData);
    setContentForms([{ ...initialContentData }]);
  };
  const handleContentChange = (index, key, value) => {
    setContentForms((prevForms) => {
      const updated = [...prevForms];
      updated[index][key] = value;
      return updated;
    });
  };
  const handleAddMore = () => {
    if (!hasAddedContent) {
      setContentForms((prev) => [...prev, { ...initialContentData }]);
      setHasAddedContent(true);
    }
  };
  const handleSubmit = async () => {
    let isValid = true;
    Object.values(fieldRefs.current).forEach((fieldRef) => {
      if (fieldRef && typeof fieldRef.validate === "function") {
        const valid = fieldRef.validate();
        if (!valid) isValid = false;
      }
    });
    if (!isValid) return;
    const formData = new FormData();
    const contentArray = [];
    Object.entries(commonFormData).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value);
      } else if (key === "name" || key === "description") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    contentForms.forEach((form, index) => {
      const langMap = { 1: 'Eng', 2: 'Mar' };
      const langKey = langMap[form.laungaugeId] || 'Unknown';
      const contentItem = {};
      Object.entries(form).forEach(([key, value]) => {
        if (value instanceof File) {
          formData.append(`${langKey}_${key}`, value);
        } else {
          contentItem[key] = value;
        }
      });
      contentArray.push(contentItem);
    });
    formData.append("content", JSON.stringify(contentArray));
    try {
      const response = mode === "update" && courseFile
        ? await updateCoursesFile(courseFile.id, formData)
        : await addCoursesFile(formData);

      if (response.status === 200 || response.status === 201) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: response.message || "File Submitted Successfully",
          life: 1500,
        });
        setTimeout(() => {
          onClose();
          if (onFormSubmit) onFormSubmit();
        }, 1500);
      } else {
        throw new Error(response.message || "Something went wrong");
      }
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: err.message || "Submission failed",
        life: 1500,
      });
    }
  };
  return (
    <>
      <Toast ref={toast} />
      <div className="form-wrapper">
        <h2>{mode === "update" ? "Update" : "Add New"} {formTitle}</h2>
        <div className="form-grid">
          {commonFields.map((field, i) => (
            <FormField
              key={field.key}
              config={field}
              value={commonFormData[field.key]}
              onChange={(val) => handleCommonChange(field.key, val)}
              cofigField={commonFields}
              ref={(el) => {
                fieldRefs.current[field.key] = el;
              }}
            />
          ))}
        </div>
        {contentForms.map((form, index) => (
          <div key={index} className="form-section">
            <h4 className="text-md font-semibold mb-4">
              Content Section {index + 1}
            </h4>
            <div className="form-grid">
              {contentFields.map((field, i) => {
                const contentType = form.contentType;
                if (contentType === "Custom" && field.key === "path") {
                  return null;
                }
                if (
                  contentType === "Youtube" &&
                  (field.key === "video" || field.key === "thumbnailPath")
                ) {
                  return null;
                }
                return (
                  <FormField
                    key={`${field.key}-${index}`}
                    config={field}
                    value={form[field.key]}
                    onChange={(val) => handleContentChange(index, field.key, val)}
                    cofigField={contentForms}
                    ref={(el) => {
                      fieldRefs.current[field.key] = el;
                    }}
                  />
                );
              })}
            </div>
          </div>
        ))}
        {!hasAddedContent && mode !== "update" && (
          <div className="form-buttons">
            <button onClick={handleAddMore} className="add-btn">
              Add More Content
            </button>
          </div>
        )}
        <div className="form-actions">
          <Button
            label={"Submit"}
            icon={"pi pi-check"}
            className="p-button-success"
            onClick={handleSubmit}
          />
          <Button
            label="Clear"
            icon="pi pi-times"
            className="p-button-secondary ml-2"
            onClick={handleClear}
          />
        </div>
      </div>
    </>
  );
}

export default CourseFileForm;